import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Mail, MapPin, Phone, CheckCircle2 } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const CHANNELS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+254 700 000 000",
    sub: "Fastest — 24/7 for claims",
    href: "https://wa.me/254700000000?text=Hi%20Fortiqa",
  },
  {
    icon: Mail,
    label: "Email",
    value: "hello@fortiqa.co.ke",
    sub: "Response within 4 hours",
    href: "mailto:hello@fortiqa.co.ke",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+254 20 000 0000",
    sub: "Mon-Fri · 08:00 — 18:00 EAT",
    href: "tel:+254200000000",
  },
];

const ENQUIRY_TYPES = [
  "Merchant onboarding",
  "Claims help",
  "Partnership enquiry",
  "WhatsApp support",
  "Billing & payments",
  "Other",
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    business: "",
    phone: "",
    email: "",
    enquiryType: ENQUIRY_TYPES[0],
    message: "",
  });

  usePageMeta({
    title: "Contact Fortiqa",
    description:
      "Get in touch with Fortiqa. WhatsApp, email, phone, or a contact form for partnerships and merchant enquiries.",
  });

  function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    fetch(`/api/public/leads/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: form.name,
        business: form.business || null,
        phone: form.phone,
        email: form.email,
        enquiry_type: form.enquiryType,
        message: form.message,
      }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || `Request failed (${r.status})`);
        }
        setSubmitted(true);
      })
      .catch((err) =>
        setError(err.message || "Couldn't send right now — please WhatsApp us instead."),
      )
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="contact-page">
      <MarketingPageShell
        eyebrow="Contact"
        title="We're a message away."
        intro="For most merchants, WhatsApp is the fastest path — our team is on it around the clock for claims and onboarding. Prefer email or a form? Pick whichever works."
        testid="contact"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 grid sm:grid-cols-3 gap-4">
          {CHANNELS.map(({ icon: Icon, label, value, sub, href }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="group rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all block"
              data-testid={`contact-channel-${label.toLowerCase()}`}
            >
              <div className="w-10 h-10 rounded-xl bg-[#114232]/8 grid place-items-center mb-4 group-hover:bg-[#114232]/14 transition-colors">
                <Icon className="w-5 h-5 text-[#114232]" strokeWidth={1.7} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5c6360] font-bold mb-1.5">
                {label}
              </div>
              <div className="font-heading text-lg font-bold text-[#114232]">{value}</div>
              <div className="text-xs text-[#5c6360] mt-1">{sub}</div>
            </a>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-16">
          <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10">
            <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-2">
              Send us a message
            </div>
            <p className="text-sm text-[#5c6360] mb-6">
              We'll get back within one business day.
            </p>

            {submitted ? (
              <div
                className="rounded-2xl bg-[#2d7a4a]/8 border border-[#2d7a4a]/30 p-6 flex items-start gap-3"
                data-testid="contact-submitted"
              >
                <CheckCircle2 className="w-5 h-5 text-[#2d7a4a] shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <div className="font-heading font-bold text-[#114232] mb-1">Message received</div>
                  <p className="text-sm text-[#5c6360] leading-relaxed">
                    Thanks {form.name || "there"} — we'll get back to you within one business day.
                    For anything urgent (especially claims), please WhatsApp us.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="grid sm:grid-cols-2 gap-4"
                data-testid="contact-form"
              >
                <Field label="Your name" required>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Business name">
                  <input
                    type="text"
                    value={form.business}
                    onChange={(e) => setForm({ ...form, business: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-business"
                  />
                </Field>
                <Field label="Phone (WhatsApp)" required>
                  <input
                    required
                    type="tel"
                    placeholder="+254 700 000 000"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-email"
                  />
                </Field>
                <Field label="Enquiry type" required>
                  <select
                    value={form.enquiryType}
                    onChange={(e) => setForm({ ...form, enquiryType: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-enquiry-type"
                  >
                    {ENQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message" required>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className={inputCls + " resize-none"}
                      data-testid="contact-input-message"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-2">
                  {error && (
                    <div className="text-xs text-[#c15337]" data-testid="contact-error">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#c15337] hover:bg-[#a8472f] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    {submitting ? "Sending…" : "Send message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="rounded-2xl bg-white border border-[#114232]/10 p-7 flex items-start gap-5">
            <div className="w-10 h-10 rounded-xl bg-[#114232]/8 grid place-items-center shrink-0">
              <MapPin className="w-5 h-5 text-[#114232]" strokeWidth={1.7} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5c6360] font-bold mb-1.5">
                Office
              </div>
              <div className="font-heading text-lg font-bold text-[#114232]">
                Westlands, Nairobi · Kenya
              </div>
              <div className="text-sm text-[#5c6360] mt-1">Visits by appointment.</div>
            </div>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-[#114232]/15 bg-[#f4f2eb]/40 text-sm text-[#1b1d1c] focus:outline-none focus:border-[#114232]/40 focus:bg-white transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5c6360] mb-1.5">
        {label} {required && <span className="text-[#c15337]">*</span>}
      </span>
      {children}
    </label>
  );
}
